import React, { useState, useEffect, useCallback } from 'react';
import { Spin, Input, Tag } from 'antd';
import {
    CheckCircleOutlined,
    ExclamationCircleOutlined,
    CloseCircleOutlined,
    SearchOutlined,
    InboxOutlined,
} from '@ant-design/icons';
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    Tooltip,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ReferenceLine,
} from 'recharts';
import { useReports } from '../context/ReportsContext';
import { getProjectsReport } from '../api/reportsApi';
import { TabHelpButton } from '../components/TabHelpGuideModal';

const tooltipStyle = {
    contentStyle: { background: '#0a0a0a', border: '1px solid #262626', borderRadius: 12, fontSize: 12, color: '#fff' },
    labelStyle: { color: '#a7c8ff', fontWeight: 700 },
};

export default function ProjectsTab({ onSelectProject }) {
    const { filters, refreshKey, getCachedTab, setCachedTab } = useReports();
    const [loading, setLoading] = useState(false);
    const [projects, setProjects] = useState(() => getCachedTab('projects')?.projects || []);
    const [search, setSearch] = useState('');

    const loadData = useCallback(async () => {
        const cached = getCachedTab('projects');
        if (cached) {
            setProjects(cached.projects || []);
            return;
        }

        setLoading(true);
        try {
            const res = await getProjectsReport({
                period: filters.period,
                startDate: filters.startDate,
                endDate: filters.endDate,
                department: filters.department,
                projectType: filters.projectType,
                projectStatus: filters.projectStatus,
                teamMember: filters.teamMember,
            });
            if (res && res.success && res.result) {
                setProjects(res.result.projects || []);
                setCachedTab('projects', res.result);
            }
        } catch (err) {
            console.error('Error fetching projects report:', err);
        } finally {
            setLoading(false);
        }
    }, [filters, refreshKey, getCachedTab, setCachedTab]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const filteredProjects = projects.filter(p => {
        if (!search) return true;
        const q = search.toLowerCase();
        return (
            (p.name || '').toLowerCase().includes(q) ||
            (p.code || '').toLowerCase().includes(q) ||
            (p.manager || '').toLowerCase().includes(q)
        );
    });

    const onTrackCount = projects.filter(p => (p.health || 'On Track') === 'On Track').length;
    const atRiskCount = projects.filter(p => p.health === 'At Risk').length;
    const delayedCount = projects.filter(p => p.health === 'Delayed').length;

    const clientCount = projects.filter(p => p.projectType === 'Client').length;
    const inHouseCount = projects.filter(p => p.projectType === 'In-house').length;
    const projectTypeData = [
        { name: 'Client Projects', value: clientCount, color: '#00AAF1' },
        { name: 'In-House Tools', value: inHouseCount, color: '#6366f1' },
    ].filter(d => d.value > 0);

    if (loading && projects.length === 0) {
        return (
            <div className="py-20 text-center bg-white dark:bg-black rounded-2xl border border-slate-200/80 dark:border-neutral-800 shadow-xs">
                <Spin size="large" />
                <p className="mt-3 text-sm font-semibold text-slate-500 dark:text-slate-400">Loading project operational performance...</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Top Project Portfolio Banner with Help Guide */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-gradient-to-r from-blue-50/80 to-indigo-50/80 dark:from-black dark:to-neutral-950 rounded-2xl border border-blue-200/70 dark:border-neutral-800">
                <div>
                    <h3 className="text-sm sm:text-base font-extrabold text-[#003263] dark:text-[#a7c8ff] font-headline">
                        Project Portfolio Health &amp; Variance
                    </h3>
                    <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5">
                        Schedule deviations, milestone deliverables, and client vs in-house portfolio distribution.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500 dark:text-slate-400 px-3 py-1.5 rounded-xl bg-white dark:bg-black border border-slate-200 dark:border-neutral-800">
                        {projects.length} Projects Total
                    </span>
                    <TabHelpButton tabKey="projects" />
                </div>
            </div>

            {/* Top Portfolio Health KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-5 rounded-2xl bg-white dark:bg-black border border-slate-200/80 dark:border-neutral-800 shadow-xs flex items-center justify-between">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-400">On Track</div>
                        <div className="text-3xl font-black text-emerald-600 dark:text-emerald-400 mt-1 font-headline">
                            {onTrackCount} Projects
                        </div>
                        <div className="text-xs text-emerald-600 dark:text-emerald-400 font-semibold mt-1">
                            Within schedule &amp; scope
                        </div>
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-emerald-50 dark:bg-neutral-900 text-emerald-500 flex items-center justify-center text-xl">
                        <CheckCircleOutlined />
                    </div>
                </div>

                <div className="p-5 rounded-2xl bg-white dark:bg-black border border-slate-200/80 dark:border-neutral-800 shadow-xs flex items-center justify-between">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-400">At Risk</div>
                        <div className="text-3xl font-black text-amber-500 mt-1 font-headline">
                            {atRiskCount} Projects
                        </div>
                        <div className="text-xs text-amber-500 font-semibold mt-1">
                            Approaching milestones
                        </div>
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-amber-50 dark:bg-neutral-900 text-amber-500 flex items-center justify-center text-xl">
                        <ExclamationCircleOutlined />
                    </div>
                </div>

                <div className="p-5 rounded-2xl bg-white dark:bg-black border border-slate-200/80 dark:border-neutral-800 shadow-xs flex items-center justify-between">
                    <div>
                        <div className="text-xs font-bold uppercase tracking-wider text-slate-400">Delayed</div>
                        <div className="text-3xl font-black text-red-500 mt-1 font-headline">
                            {delayedCount} Projects
                        </div>
                        <div className="text-xs text-red-500 font-semibold mt-1">
                            Requires PM reallocation
                        </div>
                    </div>
                    <div className="w-12 h-12 rounded-2xl bg-red-50 dark:bg-neutral-900 text-red-500 flex items-center justify-center text-xl">
                        <CloseCircleOutlined />
                    </div>
                </div>
            </div>

            {/* Schedule Variance Horizon & Health Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Horizontal Schedule Variance Visual */}
                <div className="lg:col-span-2 bg-white dark:bg-black p-6 rounded-2xl border border-slate-200/80 dark:border-neutral-800 shadow-xs">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4">
                        <div>
                            <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                                Schedule Variance Horizon (Days)
                            </h3>
                            <p className="text-xs text-slate-400">
                                Estimated milestone delivery deviation (Ahead (+) vs Delayed (-))
                            </p>
                        </div>
                        <div className="flex items-center gap-3 text-xs font-bold">
                            <span className="text-emerald-500 font-extrabold flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span> Ahead (+Days)</span>
                            <span className="text-slate-400">|</span>
                            <span className="text-red-500 font-extrabold flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500"></span> Lagging (-Days)</span>
                        </div>
                    </div>

                    <div className="h-64">
                        {filteredProjects.length === 0 ? (
                            <div className="h-full flex flex-col items-center justify-center text-slate-400 text-xs gap-2">
                                <InboxOutlined style={{ fontSize: 22 }} />
                                <span>No projects to evaluate schedule variance</span>
                            </div>
                        ) : (
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={filteredProjects.slice(0, 8).map(p => {
                                        const v = typeof p.scheduleVariance === 'number' ? p.scheduleVariance : 0;
                                        const shortName = p.name?.length > 18 ? `${p.name.slice(0, 16)}...` : p.name;
                                        return {
                                            name: shortName,
                                            fullName: p.name,
                                            variance: v,
                                            fill: v >= 0 ? '#10b981' : '#ef4444',
                                        };
                                    })}
                                    margin={{ top: 10, right: 10, left: -15, bottom: 0 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" strokeOpacity={0.2} vertical={false} />
                                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} unit="d" />
                                    <Tooltip {...tooltipStyle} />
                                    <ReferenceLine y={0} stroke="#64748b" strokeWidth={1.5} />
                                    <Bar dataKey="variance" name="Schedule Variance (Days)" radius={[4, 4, 0, 0]} maxBarSize={38}>
                                        {filteredProjects.slice(0, 8).map((p, index) => {
                                            const v = typeof p.scheduleVariance === 'number' ? p.scheduleVariance : 0;
                                            return <Cell key={`var-${index}`} fill={v >= 0 ? '#10b981' : '#ef4444'} />;
                                        })}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </div>
                </div>

                {/* Client vs In-House Distribution Visual */}
                <div className="bg-white dark:bg-black p-6 rounded-2xl border border-slate-200/80 dark:border-neutral-800 shadow-xs flex flex-col justify-between">
                    <div>
                        <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                            Project Type Split
                        </h3>
                        <p className="text-xs text-slate-400 mb-4">
                            Client revenue projects vs internal tooling
                        </p>
                    </div>

                    {projectTypeData.length === 0 ? (
                        <div className="h-44 flex flex-col items-center justify-center text-slate-400 text-xs gap-2">
                            <InboxOutlined style={{ fontSize: 22 }} />
                            <span>No projects found</span>
                        </div>
                    ) : (
                        <>
                            <div className="h-44">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={projectTypeData}
                                            dataKey="value"
                                            nameKey="name"
                                            innerRadius="58%"
                                            outerRadius="85%"
                                            paddingAngle={2}
                                            stroke="none"
                                        >
                                            {projectTypeData.map((d) => (
                                                <Cell key={d.name} fill={d.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip {...tooltipStyle} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="space-y-2 text-xs font-semibold mt-2">
                                {projectTypeData.map((d) => {
                                    const total = projects.length || 1;
                                    const pct = Math.round((d.value / total) * 100);
                                    return (
                                        <div key={d.name} className="flex justify-between items-center text-slate-700 dark:text-slate-300">
                                            <span className="flex items-center gap-2">
                                                <span className="w-2.5 h-2.5 rounded-full" style={{ background: d.color }}></span>
                                                <span>{d.name}</span>
                                            </span>
                                            <span className="font-extrabold">{pct}% ({d.value})</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Interactive Project Visual Cards Grid */}
            <div className="bg-white dark:bg-black p-6 rounded-2xl border border-slate-200/80 dark:border-neutral-800 shadow-xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                    <div>
                        <h3 className="text-base font-bold text-slate-900 dark:text-white font-headline">
                            Active Portfolio Deliverable Cards
                        </h3>
                        <p className="text-xs text-slate-400">
                            Click any project card to open its full Project Details workspace
                        </p>
                    </div>
                    <Input
                        prefix={<SearchOutlined className="text-slate-400" />}
                        placeholder="Search project code or PM..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="bg-slate-50 dark:bg-neutral-900 border-slate-200 dark:border-neutral-800 rounded-xl text-xs"
                        style={{ width: 220, height: 38 }}
                    />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredProjects.length === 0 ? (
                        <div className="col-span-full py-10 text-center text-xs text-slate-400">
                            No project records match the current filter criteria.
                        </div>
                    ) : (
                        filteredProjects.map((p) => {
                            const isDone = p.progress >= 100;
                            const healthColor = p.health === 'Delayed' ? 'red' : p.health === 'At Risk' ? 'orange' : 'green';
                            return (
                                <div
                                    key={p.id}
                                    onClick={() => onSelectProject?.(p.id || p._id)}
                                    className="p-5 rounded-2xl bg-slate-50/70 dark:bg-neutral-950 border border-slate-200/70 dark:border-neutral-800 hover:border-[#00AAF1] dark:hover:border-blue-500 cursor-pointer transition shadow-2xs space-y-3.5"
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs font-black uppercase tracking-wider px-2 py-0.5 rounded bg-blue-100 dark:bg-neutral-900 text-blue-700 dark:text-[#00AAF1] font-headline">
                                            {p.code || 'PRJ'}
                                        </span>
                                        <Tag color={healthColor}>{p.health || 'On Track'}</Tag>
                                    </div>

                                    <div>
                                        <h4 className="text-base font-bold text-slate-900 dark:text-white line-clamp-1 font-headline">
                                            {p.name}
                                        </h4>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                                            PM: {p.manager || 'Leadership'} • {p.department || 'General'}
                                        </p>
                                    </div>

                                    <div>
                                        <div className="flex justify-between text-xs font-bold mb-1">
                                            <span className="text-slate-400">Deliverable Progress</span>
                                            <span className="text-[#00AAF1] font-black">{p.progress || 0}%</span>
                                        </div>
                                        <div className="w-full bg-slate-200 dark:bg-neutral-800 h-2 rounded-full overflow-hidden">
                                            <div
                                                className={`h-full rounded-full transition-all ${isDone ? 'bg-emerald-500' : 'bg-[#00AAF1]'}`}
                                                style={{ width: `${p.progress || 0}%` }}
                                            ></div>
                                        </div>
                                    </div>

                                    <div className="pt-1 flex items-center justify-between text-xs text-slate-400 border-t border-slate-200/50 dark:border-neutral-800">
                                        <span>Milestones: <strong className="text-slate-700 dark:text-slate-200">{p.milestonesSummary || '0/0'}</strong></span>
                                        <span>Tasks: <strong className="text-slate-700 dark:text-slate-200">{p.tasksSummary || '0/0'}</strong></span>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}
